import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Stethoscope, Search, Building2, Phone, CalendarDays } from "lucide-react";
import { CLINIC } from "@/data/content";
import { member } from "@/data/adLanding";

export const Wordmark = () => (
  <img src="/images/lumiere-logo.png" alt="Lumière Aesthetics" className="h-5 w-auto self-start" data-testid="ad-wordmark" />
);

export const Panel = ({ label, className = "" }) => (
  <div className={`bg-cream border border-hairline/50 flex items-end p-3 ${className}`} data-testid="portrait-placeholder">
    <span className="text-[10px] tracking-[0.16em] uppercase text-soft-grey">{label}</span>
  </div>
);

export const HeroImage = ({ hero, className = "" }) =>
  hero.src ? (
    <img src={hero.src} alt={hero.alt} width="921" height="1152" fetchPriority="high" className={`object-cover ${className}`} data-testid="ad-hero-image" />
  ) : (
    <Panel label={hero.label} className={className} />
  );

export const PractitionerCompact = ({ slug, credentialLine, compact = false }) => {
  const m = member(slug);
  const size = compact ? "w-16 h-16 sm:w-20 sm:h-20" : "w-28 h-28";
  return (
    <div className={`flex ${compact ? "flex-col items-start gap-3" : "items-center gap-5"}`} data-testid={`ad-practitioner-${slug}`}>
      {m.photo ? (
        <img src={m.photo} alt={m.name} className={`${size} object-cover rounded-[2px] border border-hairline/60 shrink-0`} />
      ) : (
        <Panel label={m.name.split(" ").slice(-1)[0]} className={`${size} shrink-0 rounded-[2px]`} />
      )}
      <div className="min-w-0">
        <p className={`font-serif text-deep-sea ${compact ? "text-lg leading-tight" : "text-2xl"}`}>{m.name}</p>
        <p className="text-[10px] tracking-[0.16em] uppercase text-gold mt-1">{m.title}</p>
        {!compact && <p className="text-ink/75 text-sm mt-1.5 leading-snug">{credentialLine || m.credential}</p>}
        {compact && <p className="text-ink/70 text-xs mt-1 leading-snug">{m.credential}</p>}
        <p className="text-soft-grey text-[13px] mt-1">AHPRA {m.ahpra}</p>
      </div>
    </div>
  );
};

export const OfferLine = ({ items }) => (
  <p className="bg-cream border border-hairline/50 px-6 py-4 font-serif italic font-light text-[22px] leading-snug text-ink" data-testid="ad-offer-line">
    {items.join(" · ")}
  </p>
);

const PROOF_ICONS = [Stethoscope, Search, Building2];

export const ProofStrip = ({ items }) => (
  <ul className="grid grid-cols-3 gap-4" data-testid="ad-proof-strip">
    {items.map((text, i) => {
      const Icon = PROOF_ICONS[i] || Stethoscope;
      return (
        <li key={text} className="flex flex-col gap-2">
          <Icon className="w-5 h-5 text-gold" strokeWidth={1.4} aria-hidden="true" />
          <span className="text-[11px] tracking-[0.14em] uppercase text-ink/80 leading-snug">{text}</span>
        </li>
      );
    })}
  </ul>
);

export const Steps = ({ steps, note }) => (
  <section className="py-16 lg:py-20 border-t border-hairline/40" data-testid="ad-steps">
    <h2 className="font-serif font-light text-deep-sea text-3xl sm:text-4xl">What happens at the consultation</h2>
    <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-10">
      {steps.map((step, i) => (
        <li key={step} className="flex gap-4">
          <span className="font-serif text-gold text-4xl leading-none">{i + 1}</span>
          <p className="text-ink/80 text-sm leading-relaxed pt-1">{step}</p>
        </li>
      ))}
    </ol>
    {note && <p className="text-ink/75 text-sm mt-8 leading-relaxed max-w-2xl" data-testid="ad-steps-note">{note}</p>}
  </section>
);

export const CredentialsStrip = ({ lines }) => (
  <section className="py-10 border-t border-hairline/40" data-testid="ad-credentials">
    {lines.map((line) => (
      <p key={line} className="text-soft-grey text-[13px] leading-relaxed">{line}</p>
    ))}
  </section>
);

export const AdFooter = () => (
  <footer className="border-t border-hairline/40 py-10 text-soft-grey text-xs leading-relaxed" data-testid="ad-footer">
    <p>
      {CLINIC.legal} · ABN {CLINIC.abn} · {CLINIC.address} · Imagery may include models ·{" "}
      <Link to="/privacy" className="underline underline-offset-2 hover:text-gold transition-colors" data-testid="ad-footer-privacy">Privacy</Link>
    </p>
    <p className="mt-3 max-w-3xl">
      Any surgical or invasive procedure carries risks. Before proceeding, you should seek a second opinion from an
      appropriately qualified health practitioner.
    </p>
  </footer>
);

export const StickyBar = ({ targetId }) => {
  const [show, setShow] = useState(false);
  const [widgetVisible, setWidgetVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setShow(max > 0 && window.scrollY / max > 0.3);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    const target = document.getElementById(targetId);
    const io = target ? new IntersectionObserver(([e]) => setWidgetVisible(e.isIntersecting), { threshold: 0.15 }) : null;
    if (target) io.observe(target);
    return () => {
      window.removeEventListener("scroll", onScroll);
      io?.disconnect();
    };
  }, [targetId]);
  const scrollToWidget = () => {
    const el = document.getElementById(targetId);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -12 });
    else el.scrollIntoView({ behavior: "smooth" });
  };
  if (!show || widgetVisible) return null;
  return (
    <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-warm-white/95 backdrop-blur border-t border-hairline/50 p-3 flex gap-3" data-testid="ad-sticky-bar">
      <button onClick={scrollToWidget} className="btn-gold flex-1 py-3.5 text-sm font-medium tracking-[0.06em] inline-flex items-center justify-center gap-2" data-testid="ad-sticky-choose">
        <CalendarDays className="w-4 h-4" strokeWidth={1.6} /> Choose a time
      </button>
      <a href={`tel:${CLINIC.phoneTel}`} className="btn-ghost-sea px-6 py-3.5 text-sm font-medium tracking-[0.06em] inline-flex items-center justify-center gap-2" data-testid="ad-sticky-call">
        <Phone className="w-4 h-4" strokeWidth={1.6} /> Call
      </a>
    </div>
  );
};
